import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCurrentTheme } from 'utils/theme';
import { type ChartTokens, createChartTheme, readChartTokens } from './chartTheme';
import { type ChartOption, echarts } from './core';
import { createHoverIntent } from './hoverIntent';

export interface ChartSize {
  /** Container width in pixels; 0 before the first measurement. */
  width: number;
}

interface Props {
  /** Builds the option from resolved tokens and the chart width; memoize it to avoid redraws. */
  option: (tokens: ChartTokens, size: ChartSize) => ChartOption;
  height: number | ((size: ChartSize) => number);
  /** Accessible summary of what the chart shows. */
  label: string;
  className?: string;
  /** Item click; receives the clicked series id and data index. */
  onItemClick?: (item: { seriesId: string; dataIndex: number; dataId?: string }) => void;
  /**
   * Milliseconds the pointer must rest on an item before its tooltip opens, so passing
   * over the chart does not flash one. Omit for tooltips that open immediately.
   */
  tooltipDelay?: number;
  /** Item to show highlighted, e.g. while its legend row outside the chart is hovered. */
  highlight?: { seriesId: string; dataIndex: number } | null;
  /** Pointer entered (an item) or left (null) a chart item. */
  onItemHover?: (item: { seriesId: string; dataIndex: number } | null) => void;
  /** The first data zoom changed: whether it now shows less than the full range. */
  onZoom?: (zoomed: boolean) => void;
}

/** Minimal ECharts host: SVG renderer, Timely theme, light/dark switching and resizing. */
export default function EChart({
  option,
  height,
  label,
  className,
  onItemClick,
  tooltipDelay,
  highlight,
  onItemHover,
  onZoom,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.EChartsType | null>(null);
  const [width, setWidth] = useState(0);
  const theme = useCurrentTheme();
  const [tokens, setTokens] = useState(readChartTokens);
  const size = useMemo(() => ({ width }), [width]);

  // The theme class is swapped in a layout effect, so re-read tokens in a passive effect.
  // biome-ignore lint/correctness/useExhaustiveDependencies: theme is the re-read trigger.
  useEffect(() => {
    setTokens(previous => {
      const next = readChartTokens();
      return JSON.stringify(next) === JSON.stringify(previous) ? previous : next;
    });
  }, [theme]);

  useLayoutEffect(() => {
    const element = container.current!;
    const instance = echarts.init(element, createChartTheme(readChartTokens()), {
      renderer: 'svg',
    });
    chart.current = instance;
    setWidth(element.clientWidth);
    const observer = new ResizeObserver(() => {
      setWidth(element.clientWidth);
      instance.resize();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      instance.dispose();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    chart.current?.setTheme(createChartTheme(tokens));
  }, [tokens]);

  useEffect(() => {
    const built = option(tokens, size);
    // With a delay, tooltips open only through the hover intent below.
    if (tooltipDelay && built.tooltip && !Array.isArray(built.tooltip)) {
      built.tooltip = { ...built.tooltip, triggerOn: 'none' };
    }
    chart.current?.setOption(built, { replaceMerge: ['series'] });
  }, [option, tokens, size, tooltipDelay]);

  useEffect(() => {
    const instance = chart.current;
    if (!instance || !onItemClick) return;
    const handler = (params: { seriesId?: string; dataIndex: number; data?: unknown }) =>
      onItemClick({
        seriesId: params.seriesId ?? '',
        dataIndex: params.dataIndex,
        dataId:
          typeof params.data === 'object' &&
          params.data !== null &&
          'id' in params.data &&
          typeof params.data.id === 'string'
            ? params.data.id
            : undefined,
      });
    instance.on('click', handler);
    return () => {
      if (!instance.isDisposed()) instance.off('click', handler);
    };
  }, [onItemClick]);

  useEffect(() => {
    const instance = chart.current;
    if (!instance || !onZoom) return;
    const handler = () => {
      const [zoom] = (instance.getOption().dataZoom ?? []) as Array<{
        start?: number;
        end?: number;
      }>;
      // Percentages; tolerate rounding after zooming all the way back out.
      onZoom(!!zoom && ((zoom.start ?? 0) > 0.01 || (zoom.end ?? 100) < 99.99));
    };
    instance.on('datazoom', handler);
    return () => {
      if (!instance.isDisposed()) instance.off('datazoom', handler);
    };
  }, [onZoom]);

  // Only charts given a highlight prop manage highlight state from outside.
  const highlightControlled = highlight !== undefined;
  const highlightSeries = highlight?.seriesId;
  const highlightIndex = highlight?.dataIndex;
  useEffect(() => {
    const instance = chart.current;
    if (!instance || !highlightControlled) return;
    instance.dispatchAction({ type: 'downplay' });
    if (highlightSeries !== undefined && highlightIndex !== undefined) {
      instance.dispatchAction({
        type: 'highlight',
        seriesId: highlightSeries,
        dataIndex: highlightIndex,
      });
    }
  }, [highlightControlled, highlightSeries, highlightIndex]);

  // Hover: delayed tooltips and item hover callbacks. Every way of leaving the chart
  // closes both, including a native mouseleave, so nothing stays stuck when ECharts
  // misses an item mouseout (fast exits, redraws while hovering).
  useEffect(() => {
    const instance = chart.current;
    const element = container.current;
    if (!instance || !element || (!tooltipDelay && !onItemHover)) return;
    const zr = instance.getZr();
    const intent = tooltipDelay
      ? createHoverIntent({
          delay: tooltipDelay,
          show: target => instance.dispatchAction({ type: 'showTip', ...target }),
          hide: () => instance.dispatchAction({ type: 'hideTip' }),
        })
      : null;
    const over = (params: { seriesId?: string; seriesIndex?: number; dataIndex: number }) => {
      intent?.over({ seriesIndex: params.seriesIndex, dataIndex: params.dataIndex });
      onItemHover?.({ seriesId: params.seriesId ?? '', dataIndex: params.dataIndex });
    };
    const out = () => {
      intent?.out();
      onItemHover?.(null);
    };
    const leave = () => {
      intent?.leave();
      onItemHover?.(null);
      if (!instance.isDisposed()) instance.dispatchAction({ type: 'hideTip' });
    };
    instance.on('mouseover', over);
    instance.on('mouseout', out);
    zr.on('globalout', leave);
    element.addEventListener('mouseleave', leave);
    return () => {
      intent?.dispose();
      element.removeEventListener('mouseleave', leave);
      // Unmounting disposes the chart first, which drops every handler anyway.
      if (instance.isDisposed()) return;
      instance.off('mouseover', over);
      instance.off('mouseout', out);
      zr.off('globalout', leave);
    };
  }, [tooltipDelay, onItemHover]);

  return (
    <div
      ref={container}
      role="img"
      aria-label={label}
      className={className}
      style={{ height: typeof height === 'function' ? height(size) : height }}
    />
  );
}
